import React, { useMemo, useState, useEffect, useRef } from "react";
import Sidebar from "../components/Sidebar";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import Chart from "chart.js/auto";
import Pagination from "../components/pagination";
import { toast } from "react-toastify";
import {
  AddSubscriptionModal,
  AddPlanModal,
  SubscriptionSuccessModal,
  DeletePlanModal,
  EditPlanModal,
  PlanSuccessModal,
} from "../components/modals/subscriptionModal";
import { getAuthToken, getAdminName } from "../utils/cookieUtils";
import { useLocations } from "../hooks/useLocations";

function Subscriptions() {
  const token = getAuthToken();
  const chartRef = useRef(null);
  const currentDate = useMemo(() => new Date(), []);
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthsOfYear = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );
  const [adminProfile, setAdminProfile] = useState({});
  const dayOfWeekIndex = currentDate.getDay();
  const monthIndex = currentDate.getMonth();
  const dayOfMonth = currentDate.getDate();
  const formattedDate = `${daysOfWeek[dayOfWeekIndex]}, ${monthsOfYear[monthIndex]} ${dayOfMonth}`;
  const adminData = getAdminName();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [payerName, setPayerName] = useState("");
  const [gymNumber, setGymNumber] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [recentSub, setRecentSub] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 6;
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(false);
  const [chartError, setChartError] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [selectedPlanName, setSelectedPlanName] = useState("");
  const [addPlan, setAddPlan] = useState(false);
  const [planName, setPlanName] = useState("");
  const [planDuration, setPlanDuration] = useState("");
  const [planAmount, setPlanAmount] = useState("");
  const [locationId, setLocationId] = useState("");
  const [addPlanSuccessModalIsOpen, setAddPlanSuccessModalIsOpen] =
    useState(false);
  const [getSubPlans, setGetSubPlans] = useState([]);
  const [planIdToDelete, setPlanIdToDelete] = useState(null);
  const [deleteSuccessModalIsOpen, setDeleteSuccessModalIsOpen] =
    useState(false);
  
  // Location filter state
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState("");
  const { locations } = useLocations();
  
  const popupRef = useRef(null);

  const openAddModal = () => {
    setAddPlan(true);
  };
  const closeAddModal = () => {
    setAddPlan(false);
  };

  const openModal = () => {
    setPayerName("");
    setGymNumber("");
    setAmountPaid("");
    setPaymentMethod("");
    setSubscriptionPlan("");
    setCurrentStep(1);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const subscriptionData = {
      user_id: gymNumber,
      payment_method: paymentMethod,
      subscription_plan: subscriptionPlan,
    };

    try {
      const response = await axios.post(
        "/super-admin/create-customer-subscription",
        subscriptionData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSuccessModalIsOpen(true); 
    } catch (error) {
      console.error("Error creating subscription:", error);
    }

    setPayerName("");
    setGymNumber("");
    setAmountPaid("");
    setPaymentMethod("");
    setSubscriptionPlan("");
    setModalIsOpen(false);
  };

  const handleAddPlanSubmit = async (e) => {
    e.preventDefault();
    const planData = {
      name: planName,
      duration: planDuration,
      amount: planAmount,
      location_id: locationId,
    };

    try {
      const response = await axios.post(
        "/super-admin/create-subscription-plan",
        planData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      
      setPlanName("");
      setPlanDuration("");
      setPlanAmount("");
      closeAddModal(); 
      setAddPlanSuccessModalIsOpen(true); 
    } catch (error) {
      
      
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        if (
          error.response.data.message === "Unauthorized to access this resource"
        ) {
          toast.error("You don't have permission to carry out this action");
        } else {
          toast.error(`Error: ${error.response.data.message}`);
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }

      console.error(
        "Error creating subscription plan:",
        error.response ? error.response.data : error.message
      );
    }
  };

  useEffect(() => {
    const token = getAuthToken();
    const fetchAdminProfileData = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(
          "/super-admin/get-admin-profile-data",
          config
        );
        setAdminProfile(response.data.message.admin); 
      } catch (error) {
        console.error("API Error:", error);
      }
    };

    fetchAdminProfileData(); 
  }, []);

  const handleNextStep = (event) => {
    event.preventDefault();
    setCurrentStep(2);
  };

  const handleBackStep = () => {
    setCurrentStep(1);
  };

  const closeSuccessModal = () => {
    setSuccessModalIsOpen(false);
  };

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setChartError(false); // Reset chart error state when retrying

      const response = await axios.get("/super-admin/get-subscription-data", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = response.data.message;
      
      
      if (!data || !data.net_subscribers_by_month || !data.net_cancellation_by_month) {
        console.error("Invalid API response structure:", data);
        setLoading(false);
        return;
      }

      const monthsOfYear = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      
      
      const netSubscribersByMonth = Array.from({ length: 12 }, (_, index) => {
        const monthData = data.net_subscribers_by_month.find(
          (item) => item.month === index + 1
        );
        return {
          x: monthsOfYear[index],
          y: monthData ? monthData.net_subscribers : 0,
        };
      });

      
      const netCancellationsByMonth = Array.from({ length: 12 }, (_, index) => {
        const monthData = data.net_cancellation_by_month.find(
          (item) => item.month === index + 1
        );
        return {
          x: monthsOfYear[index],
          y: monthData ? monthData.net_subscribers : 0,
        };
      });

      
      setTimeout(() => {
        renderChart(monthsOfYear, netSubscribersByMonth, netCancellationsByMonth);
      }, 100); 

    } catch (error) {
      console.error("Error fetching subscription data:", error);
      setLoading(false);
      setChartError(true);
      toast.error("Failed to load subscription data. Please refresh the page.");
    }
  };

  const renderChart = (monthsOfYear, netSubscribersByMonth, netCancellationsByMonth, retryCount = 0) => {
    try {
      
      const canvasElement = document.getElementById("subscriptionChart");
      if (!canvasElement) {
        if (retryCount < 3) {
          
          setTimeout(() => {
            renderChart(monthsOfYear, netSubscribersByMonth, netCancellationsByMonth, retryCount + 1);
          }, 200);
          return;
        }
        console.error("Canvas element 'subscriptionChart' not found after retries");
        setLoading(false);
        setChartError(true);
        return;
      }

      
      const rect = canvasElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        if (retryCount < 3) {
          setTimeout(() => {
            renderChart(monthsOfYear, netSubscribersByMonth, netCancellationsByMonth, retryCount + 1);
          }, 200);
          return;
        }
        console.warn("Canvas element has zero dimensions after retries");
      }

      
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      const ctx = canvasElement.getContext("2d");
      if (!ctx) {
        console.error("Unable to get 2D context from canvas");
        setLoading(false);
        setChartError(true);
        toast.error("Chart rendering failed. Please refresh the page.");
        return;
      }

      const gradient = ctx.createLinearGradient(0, 0, 0, 400);
      gradient.addColorStop(0, "rgba(122, 176, 255, 0.5)");
      gradient.addColorStop(1, "rgba(255, 255, 255, 0.5)");

      const gradientCancellation = ctx.createLinearGradient(0, 0, 0, 400);
      gradientCancellation.addColorStop(0, "rgba(255, 99, 132, 0.5)");
      gradientCancellation.addColorStop(1, "rgba(255, 255, 255, 0.5)");

      chartRef.current = new Chart(ctx, {
        type: "line",
        data: {
          labels: monthsOfYear,
          datasets: [
            {
              label: "Net Subscribers",
              data: netSubscribersByMonth,
              borderColor: "#2A6FF1",
              backgroundColor: gradient,
              fill: true,
              pointStyle: "circle",
            },
            {
              label: "Net Cancellations",
              data: netCancellationsByMonth,
              borderColor: "#DE6B48",
              backgroundColor: gradientCancellation,
              fill: true,
              pointStyle: "circle",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                font: {
                  size: 12,
                  family: "Geist",
                  color: "#737373",
                },
                padding: 10,
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: "Number of Subscribers",
                font: {
                  size: 12,
                  weight: "bold",
                  family: "Geist",
                },
                color: "#737373",
                padding: {
                  top: 10,
                  bottom: 10,
                },
              },
              ticks: {
                font: {
                  size: 12,
                  family: "Geist",
                  color: "#737373",
                },
                padding: 10,
              },
            },
          },
          plugins: {
            legend: {
              position: "top",
              align: "end",
              labels: {
                font: {
                  size: 14,
                  family: "Geist",
                  padding: 100,
                  usePointStyle: true,
                  pointStyle: "circle",
                },
                color: "#010101",
              },
            },
          },
        },
      });

      setLoading(false);
    } catch (error) {
      console.error("Error rendering chart:", error);
      setLoading(false);
      setChartError(true);
      toast.error("Failed to load subscription chart. Please refresh the page.");
    }
  };

  useEffect(() => {
    if (token) {
      fetchSubscriptionData();
    }
    
    
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [token]);

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      // Don't fetch if we don't have admin profile data yet
      if (!adminProfile?.role_id) {
        return;
      }

      setPlansLoading(true);
      try {
        // Check if admin is super admin
        const isSuperAdmin = adminProfile.role_id === 3;
        
        // Build URL with location filtering
        let url = "/super-admin/get-subscription-plans";
        const params = [];
        
        if (isSuperAdmin) {
          // Super admin: use location filter if selected, otherwise show all
          if (selectedLocationId) {
            params.push(`location_id=${selectedLocationId}`);
          }
        } else {
          // Non-super admin (branch admin): always filter by their own location
          if (adminProfile.location_id) {
            params.push(`location_id=${adminProfile.location_id}`);
          }
        }
        
        if (params.length > 0) {
          url += `?${params.join('&')}`;
        }
        
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        let plans = response.data.message?.subscription_plans;
        if (plans && Array.isArray(plans)) {
          setGetSubPlans(plans);
        } else if (plans && Array.isArray(plans.data)) {
          setGetSubPlans(plans.data);
        } else {
          console.error("Subscription plans response is not an array:", plans);
        }
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchSubscriptionPlans();
  }, [token, adminProfile?.role_id, adminProfile?.location_id, selectedLocationId]);

  const handleSubscriptionPlanChange = (e) => {
    const selectedPlanId = Number(e.target.value);
    const selectedPlan = getSubPlans.find((plan) => plan.id === selectedPlanId);
    setSubscriptionPlan(selectedPlanId);
    if (selectedPlan) {
      setAmountPaid(selectedPlan.amount);
      setSelectedPlanName(selectedPlan.name);
    } else {
      setAmountPaid("");
      setSelectedPlanName("");
    }
  };

  useEffect(() => {
    const fetchRecentSubscriptions = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `/super-admin/get-recent-subscriptions?page=${currentPage}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRecentSub(response.data.message.data);
        setTotalPages(response.data.message.last_page);
      } catch (error) {
        console.error("Error fetching recent subscriptions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentSubscriptions();
  }, [currentPage, token]);

  
  useEffect(() => {
    if (searchTerm) {
      const filtered = recentSub.filter(
        (sub) =>
          sub.payer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sub.subscription_plan
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          sub.payment_method.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sub.tnx_ref.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubscriptions(filtered);
    } else {
      setFilteredSubscriptions(recentSub); 
    }
  }, [searchTerm, recentSub]);

  
  const totalFilteredPages = Math.ceil(filteredSubscriptions.length / 10); 
  const pagesToUse = searchTerm ? totalFilteredPages : totalPages; 

  const handleExportCSV = () => {
    const csvData = [
      [
        "Invoice number",
        "Name",
        "Amount",
        "Payment date",
        "Subscription Plan",
        "Payment Method",
      ],
      ...filteredSubscriptions.map((sub) => [
        sub.tnx_ref,
        sub.payer_name,
        sub.amount,
        sub.date,
        sub.subscription_plan,
        sub.payment_method,
      ]),
    ];
    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvData.map((e) => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "subscriptions.csv");
    document.body.appendChild(link);
    link.click();
  };

  const [currentPlanPage, setCurrentPlanPage] = useState(1);
  const plansPerPage = 5;
  const totalPlanPages = Math.ceil(getSubPlans.length / plansPerPage);

  const [optionsPopupOpen, setOptionsPopupOpen] = useState(null);

  const openOptionsPopup = (index) => {
    setOptionsPopupOpen(optionsPopupOpen === index ? null : index);
  };

  const closeOptionsPopup = () => {
    setOptionsPopupOpen(null);
  };

  // Location filter handler
  const handleLocationFilter = (locationId, locationName = "") => {
    setSelectedLocationId(locationId);
    setSelectedLocation(locationName);
    setCurrentPlanPage(1); // Reset to first page when filtering
    setLocationDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownOpen && !event.target.closest('.location-filter')) {
        setLocationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [locationDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closeOptionsPopup();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef]);

  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);

  const closeConfirmModal = () => {
    setConfirmDeleteModal(false);
  };

  const deletePlan = async () => {
    try {
      const response = await fetch(
        `/super-admin/subscription-plan/${planIdToDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        setGetSubPlans(
          getSubPlans.filter((plan) => plan.id !== planIdToDelete)
        ); 
        closeConfirmModal();
        setDeleteSuccessModalIsOpen(true);
      } else {
        console.error("Failed to delete plan");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const [editModal, setEditModal] = useState(false);
  const [EditSuccessModal, setEditSuccessModal] = useState(false);

  const EditSuccessModalIsClosed = () => {
    setEditSuccessModal(false);
  };

  const [planDetails, setPlanDetails] = useState({
    name: "",
    duration_in_days: "",
    amount: "",
  });

  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const editModalIsOpen = (plan) => {
    setPlanDetails({
      name: plan.name || "",
      duration_in_days: plan.duration_in_days || "",
      amount: plan.amount || "",
    });
    setEditModal(true);
    setOptionsPopupOpen(false);
  };

  const editModalIsClose = () => {
    setEditModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPlanDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault(); 

    if (!selectedPlanId) {
      console.error("Selected plan ID is missing.");
      return;
    }

    
    const updatedData = {
      plan_id: selectedPlanId,
      name: planDetails.name || "",
      duration_in_days: planDetails.duration_in_days || "",
      amount: planDetails.amount || "",
      location_id: planDetails.location_id || "",
    };

    try {
      const response = await axios.post(
        "/super-admin/edit-subscription-plans",
        updatedData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEditModal(false);
      setEditSuccessModal(true);
    } catch (error) {
      const errorMessage =
        error.response && error.response.data
          ? error.response.data.message
          : error.message;
      console.error("Error updating plan:", errorMessage);
      alert(errorMessage);
    }
  };

  return (
    <div className="dashboard container-fluid">
      <div className="Nav">
        <Sidebar />
      </div>
      <div className="dashboard-display container-fluid">
        <div className="top-bar container-fluid">
          <div className="top-bar-info">
            <p id="date">{formattedDate}</p>
            <b>
              <p>Welcome back {adminData}</p>
            </b>
          </div>
          <div className="top-bar-button">
            <button onClick={openAddModal}>
              <img
                src={`${process.env.PUBLIC_URL}/images/add-plan.png`}
                alt="Add plan"
                style={{ height: "15px" }}
              />{" "}
              Add subscription plan
            </button>
            <button onClick={openModal}>
              <img
                src={`${process.env.PUBLIC_URL}/images/add-subscription.png`}
                alt="Add subscription"
              />{" "}
              Add payment
            </button>
          </div>
        </div>
        <div className="main-dashboard container-fluid sub-dashboard">
          <div className="subscription-graph">
            {loading ? (
              <div className="spinner" style={{ marginTop: "50px" }}></div>
            ) : chartError ? (
              <div style={{ 
                textAlign: "center", 
                padding: "50px", 
                color: "#666",
                border: "1px solid #ddd",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9"
              }}>
                <p style={{ fontSize: "16px", margin: "0" }}>
                  Unable to load subscription chart data
                </p>
              </div>
            ) : (
              <canvas 
                id="subscriptionChart" 
                style={{ minHeight: "300px", width: "100%" }}
              ></canvas>
            )}
          </div>

          <div className="recent-subs">
            <h2>Recent Subscriptions</h2>
            <div className="main-settings staff-items sub-items">
              <div className="search">
                <input
                  type="text"
                  placeholder="Search fields"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="export">
                <button onClick={handleExportCSV}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                    alt="Export"
                  />{" "}
                  Export
                </button>
              </div>
            </div>
            <div className="show-admin-table" style={{ margin: "0" }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Invoice number</th>
                    <th>Name</th>
                    <th>Amount</th>
                    <th>Payment date</th>
                    <th>Subscription Plan</th>
                    <th>Payment Method</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6">
                        <div className="spinner"></div>
                      </td>
                    </tr>
                  ) : filteredSubscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center" }}>
                        No members found
                      </td>
                    </tr>
                  ) : (
                    filteredSubscriptions.map((sub, index) => (
                      <tr key={index}>
                        <td>{sub.tnx_ref}</td>
                        <td>{sub.payer_name}</td>
                        <td>₦{sub.amount}</td>
                        <td>
                          {new Date(sub.date).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric",
                          })}
                        </td>
                        <td>{sub.subscription_plan}</td>
                        <td>{sub.payment_method}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <Pagination
                currentPageProp={currentPage}
                totalPageProp={pagesToUse}
                onPageChange={(page) => setCurrentPage(page)}
                loading={loading}
              />
            </div>
          </div>

          <div className="sub-plan-table ">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0 }}>Subscription Plans</h2>
              
              {/* Location Filter - Only for Super Admin */}
              {adminProfile?.role_id === 3 && (
                <div className="location-filter">
                  <button
                    className={`location-filter-button ${selectedLocationId ? 'active' : ''}`}
                    onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                  >
                    <span>
                      {selectedLocationId 
                        ? locations.find(loc => loc.id.toString() === selectedLocationId)?.name || 'Select Location'
                        : 'All Locations'
                      }
                    </span>
                    <span className={`location-filter-arrow ${locationDropdownOpen ? 'open' : ''}`}>
                      <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                  {locationDropdownOpen && (
                    <div className="location-filter-dropdown">
                      <div
                        className={`location-filter-option ${!selectedLocationId ? 'selected' : ''}`}
                        onClick={() => handleLocationFilter("")}
                      >
                        All Locations
                      </div>
                      {locations.map(location => (
                        <div
                          key={location.id}
                          className={`location-filter-option ${selectedLocationId === location.id.toString() ? 'selected' : ''}`}
                          onClick={() => handleLocationFilter(location.id.toString(), location.name)}
                        >
                          {location.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              className="show-admin-table"
              style={{ margin: "0", marginBottom: "30px" }}
            >
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Duration</th>
                    <th>Amount</th>
                    <th>Location</th>
                    <th>Creation Date</th>
                    <th>Options</th>
                  </tr>
                </thead>
                <tbody>
                  {plansLoading || !adminProfile?.role_id ? (
                    <tr>
                      <td colSpan="7">
                        <div className="spinner"></div>
                      </td>
                    </tr>
                  ) : getSubPlans.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center" }}>
                        No subscription plans found
                      </td>
                    </tr>
                  ) : (
                    getSubPlans
                      .slice(
                        (currentPlanPage - 1) * plansPerPage,
                        currentPlanPage * plansPerPage
                      )
                      .map((plan, index) => (
                        <tr key={index}>
                          <td>{plan.id}</td>
                          <td>{plan.name}</td>
                          <td>{plan.duration_in_days}</td>
                          <td>₦{plan.amount}</td>
                          <td>{plan.location && plan.location.name ? plan.location.name : "N/A"}</td>
                          <td>
                            {new Date(plan.created_at).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              }
                            )}
                          </td>
                          <td>
                            <div className="options-menu">
                              <img
                                alt="Options menu"
                                src={`${process.env.PUBLIC_URL}/images/horizontal-menu.png`}
                                onClick={() => setOptionsPopupOpen(index)}
                              />
                              {optionsPopupOpen === index && (
                                <div
                                  ref={popupRef}
                                  className="popup"
                                  style={{ width: "150px", left: "-95px" }}
                                >
                                  <ul>
                                    <li
                                      onClick={() => {
                                        editModalIsOpen(plan);
                                        setSelectedPlanId(plan.id);
                                      }}
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/edit.png`}
                                        alt="Edit"
                                      />{" "}
                                      Edit Plan
                                    </li>
                                    <li
                                      onClick={() => {
                                        setConfirmDeleteModal(true);
                                        setPlanIdToDelete(plan.id);
                                        setOptionsPopupOpen(null);
                                      }}
                                      style={{ color: "red" }}
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/delete.png`}
                                        alt="Delete"
                                      />{" "}
                                      Delete Plan
                                    </li>
                                  </ul>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>

              <Pagination
                currentPageProp={currentPlanPage}
                totalPageProp={totalPlanPages}
                onPageChange={(page) => setCurrentPlanPage(page)}
                loading={loading}
              />
            </div>
          </div>
        </div>

        <AddSubscriptionModal
          isOpen={modalIsOpen}
          onClose={closeModal}
          currentStep={currentStep}
          payerName={payerName}
          setPayerName={setPayerName}
          gymNumber={gymNumber}
          setGymNumber={setGymNumber}
          subscriptionPlan={subscriptionPlan}
          setSubscriptionPlan={setSubscriptionPlan}
          amountPaid={amountPaid}
          paymentMethod={paymentMethod}
          setPaymentMethod={setPaymentMethod}
          selectedPlanName={selectedPlanName}
          getSubPlans={getSubPlans}
          loading={loading}
          onNextStep={handleNextStep}
          onBackStep={handleBackStep}
          onSubmit={handleSubmit}
          handleSubscriptionPlanChange={handleSubscriptionPlanChange}
        />

        <SubscriptionSuccessModal
          isOpen={successModalIsOpen}
          onClose={closeSuccessModal}
          adminProfile={adminProfile}
        />

        <AddPlanModal
          isOpen={addPlan}
          onClose={closeAddModal}
          planName={planName}
          setPlanName={setPlanName}
          planDuration={planDuration}
          setPlanDuration={setPlanDuration}
          planAmount={planAmount}
          setPlanAmount={setPlanAmount}
          onSubmit={handleAddPlanSubmit}
          locationId={locationId}
          setLocationId={setLocationId}
        />

        <DeletePlanModal
          isOpen={confirmDeleteModal}
          onClose={closeConfirmModal}
          onConfirmDelete={deletePlan}
        />

        <PlanSuccessModal
          isOpen={deleteSuccessModalIsOpen}
          onClose={() => setDeleteSuccessModalIsOpen(false)}
          title="Plan deleted"
          message="Subscription Plan has been deleted successfully"
        />

        <EditPlanModal
          isOpen={editModal}
          onClose={editModalIsClose}
          planDetails={planDetails}
          onInputChange={handleInputChange}
          onSave={handleSave}
        />

        <PlanSuccessModal
          isOpen={EditSuccessModal}
          onClose={EditSuccessModalIsClosed}
          title="Subscription plan edited successfully"
        />

        <PlanSuccessModal
          isOpen={addPlanSuccessModalIsOpen}
          onClose={() => setAddPlanSuccessModalIsOpen(false)}
          title="Plan Added"
          message="Subscription plan has been added successfully"
        />
      </div>
    </div>
  );
}

export default Subscriptions;
